import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  CheckCircle, XCircle, AlertTriangle, Users, Shield, Download,
  Calendar, Loader2, Clock, Heart,
} from 'lucide-react';

// ─── Summary Stat ──────────────────────────────────────────
const Stat = ({ label, value, accent, warn, icon: Icon }) => (
  <div className="p-3 bg-zinc-950/50 border border-zinc-800 rounded-sm text-center" data-testid={`stat-${label?.toLowerCase().replace(/\s+/g, '-')}`}>
    {Icon && <Icon className={`w-4 h-4 mx-auto mb-1 ${accent ? 'text-amber-400' : warn ? 'text-rose-400' : 'text-zinc-500'}`} />}
    <div className={`text-xl font-bold font-mono ${accent ? 'text-amber-400' : warn ? 'text-rose-400' : 'text-zinc-100'}`}>
      {value ?? '—'}
    </div>
    <div className="text-[11px] text-zinc-500 mt-0.5">{label}</div>
  </div>
);

// ─── Export CSV helper ─────────────────────────────────────
function exportCSV(filename, headers, rows) {
  const csvContent = [headers.join(','), ...rows.map(r => headers.map(h => `"${r[h] ?? ''}"`).join(','))].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ═══════════════════════════════════════════════════════════
//  ADHERENCE TAB
// ═══════════════════════════════════════════════════════════
export function AdherenceReport({ dateFrom, dateTo, teamId, athleteId }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (teamId) params.append('team_id', teamId);
      if (athleteId) params.append('athlete_id', athleteId);
      const res = await api.get(`/reports/adherence?${params}`);
      setData(res.data);
    } catch { toast.error('Failed to load adherence report'); }
    finally { setLoading(false); }
  }, [api, dateFrom, dateTo, teamId, athleteId]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;
  if (!data) return <div className="text-center py-12 text-zinc-500 text-sm">Select filters and date range to generate report</div>;

  const s = data.summary;
  return (
    <div className="space-y-5" data-testid="adherence-report">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Stat label="Sessions" value={s.total_sessions} icon={Calendar} />
        <Stat label="Completed" value={s.total_completed} accent icon={CheckCircle} />
        <Stat label="Partial" value={s.total_partial} icon={Clock} />
        <Stat label="Missed" value={s.total_missed} warn icon={XCircle} />
        <Stat label="Pending" value={s.total_assigned} icon={AlertTriangle} />
      </div>

      {/* Export */}
      {data.athletes.length > 0 && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-400 h-8 text-xs"
            data-testid="export-adherence"
            onClick={() => exportCSV('adherence_report.csv',
              ['athlete_name', 'team', 'completed', 'partial', 'missed', 'assigned', 'total', 'completion_pct'],
              data.athletes
            )}>
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="adherence-table">
          <thead>
            <tr className="border-b border-zinc-800 text-zinc-500 text-xs">
              <th className="text-left py-2 px-3 font-medium">Athlete</th>
              <th className="text-left py-2 px-2 font-medium hidden sm:table-cell">Team</th>
              <th className="text-center py-2 px-2 font-medium">Done</th>
              <th className="text-center py-2 px-2 font-medium hidden sm:table-cell">Partial</th>
              <th className="text-center py-2 px-2 font-medium hidden sm:table-cell">Missed</th>
              <th className="text-center py-2 px-2 font-medium">Total</th>
              <th className="text-right py-2 px-3 font-medium">%</th>
            </tr>
          </thead>
          <tbody>
            {data.athletes.map(a => (
              <tr key={a.athlete_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                <td className="py-2.5 px-3 text-zinc-200 font-medium">{a.athlete_name}</td>
                <td className="py-2.5 px-2 text-zinc-500 text-xs hidden sm:table-cell">{a.team}</td>
                <td className="py-2.5 px-2 text-center text-emerald-400 font-mono">{a.completed}</td>
                <td className="py-2.5 px-2 text-center text-amber-400 font-mono hidden sm:table-cell">{a.partial}</td>
                <td className="py-2.5 px-2 text-center text-rose-400 font-mono hidden sm:table-cell">{a.missed}</td>
                <td className="py-2.5 px-2 text-center text-zinc-400 font-mono">{a.total}</td>
                <td className="py-2.5 px-3 text-right">
                  <span className={`font-mono font-bold text-xs ${a.completion_pct >= 80 ? 'text-emerald-400' : a.completion_pct >= 50 ? 'text-amber-400' : 'text-rose-400'}`}>
                    {a.completion_pct}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.athletes.length === 0 && <div className="text-center py-8 text-zinc-500 text-sm">No session data found for this period</div>}
      </div>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-2">
        {data.athletes.map(a => (
          <div key={`m-${a.athlete_id}`} className="p-3 bg-zinc-900/50 border border-zinc-800 rounded-sm">
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-sm text-zinc-200 font-medium">{a.athlete_name}</span>
              <span className={`text-xs font-mono font-bold ${a.completion_pct >= 80 ? 'text-emerald-400' : 'text-amber-400'}`}>{a.completion_pct}%</span>
            </div>
            <div className="flex gap-3 text-[10px] text-zinc-500">
              <span>Done: <span className="text-emerald-400">{a.completed}</span></span>
              <span>Partial: <span className="text-amber-400">{a.partial}</span></span>
              <span>Missed: <span className="text-rose-400">{a.missed}</span></span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  AVAILABILITY TAB
// ═══════════════════════════════════════════════════════════
export function AvailabilityReport({ dateFrom, dateTo, teamId, athleteId }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (teamId) params.append('team_id', teamId);
      if (athleteId) params.append('athlete_id', athleteId);
      const res = await api.get(`/reports/availability?${params}`);
      setData(res.data);
    } catch { toast.error('Failed to load availability report'); }
    finally { setLoading(false); }
  }, [api, dateFrom, dateTo, teamId, athleteId]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;
  if (!data) return <div className="text-center py-12 text-zinc-500 text-sm">Select filters and date range to generate report</div>;

  const s = data.summary;
  return (
    <div className="space-y-5" data-testid="availability-report">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Stat label="Athletes" value={s.total_athletes} icon={Users} />
        <Stat label="Avg Availability" value={`${s.avg_availability}%`} accent icon={Heart} />
        <Stat label="Period (days)" value={s.total_days} icon={Calendar} />
        <Stat label="Fully FIT" value={s.athletes_fully_fit} accent icon={CheckCircle} />
        <Stat label="In RTP" value={s.athletes_in_rtp} warn={s.athletes_in_rtp > 0} icon={Shield} />
      </div>

      {data.athletes.length > 0 && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-400 h-8 text-xs"
            data-testid="export-availability"
            onClick={() => exportCSV('availability_report.csv',
              ['athlete_name', 'team', 'fit_days', 'rtp_days', 'total_days', 'availability_pct'],
              data.athletes
            )}>
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
          </Button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="availability-table">
          <thead>
            <tr className="border-b border-zinc-800 text-zinc-500 text-xs">
              <th className="text-left py-2 px-3 font-medium">Athlete</th>
              <th className="text-left py-2 px-2 font-medium hidden sm:table-cell">Team</th>
              <th className="text-center py-2 px-2 font-medium">FIT Days</th>
              <th className="text-center py-2 px-2 font-medium">RTP Days</th>
              <th className="text-right py-2 px-3 font-medium">Availability</th>
            </tr>
          </thead>
          <tbody>
            {data.athletes.map(a => (
              <tr key={a.athlete_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                <td className="py-2.5 px-3 text-zinc-200 font-medium">{a.athlete_name}</td>
                <td className="py-2.5 px-2 text-zinc-500 text-xs hidden sm:table-cell">{a.team}</td>
                <td className="py-2.5 px-2 text-center text-emerald-400 font-mono">{a.fit_days}</td>
                <td className="py-2.5 px-2 text-center font-mono">
                  {a.rtp_days > 0 ? <span className="text-rose-400">{a.rtp_days}</span> : <span className="text-zinc-600">0</span>}
                </td>
                <td className="py-2.5 px-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 h-1.5 bg-zinc-800 rounded-full overflow-hidden hidden sm:block">
                      <div className={`h-full rounded-full ${a.availability_pct >= 90 ? 'bg-emerald-500' : a.availability_pct >= 70 ? 'bg-amber-500' : 'bg-rose-500'}`}
                        style={{ width: `${a.availability_pct}%` }} />
                    </div>
                    <span className={`font-mono font-bold text-xs ${a.availability_pct >= 90 ? 'text-emerald-400' : a.availability_pct >= 70 ? 'text-amber-400' : 'text-rose-400'}`}>
                      {a.availability_pct}%
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.athletes.length === 0 && <div className="text-center py-8 text-zinc-500 text-sm">No athletes found</div>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  RTP / INJURY TAB
// ═══════════════════════════════════════════════════════════
export function RTPInjuryReport({ dateFrom, dateTo, teamId, athleteId }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (teamId) params.append('team_id', teamId);
      if (athleteId) params.append('athlete_id', athleteId);
      const res = await api.get(`/reports/rtp-injury?${params}`);
      setData(res.data);
    } catch { toast.error('Failed to load RTP report'); }
    finally { setLoading(false); }
  }, [api, dateFrom, dateTo, teamId, athleteId]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;
  if (!data) return <div className="text-center py-12 text-zinc-500 text-sm">Select filters and date range to generate report</div>;

  const s = data.summary;
  const PHASE_COLORS = {
    PHASE_1: 'bg-red-500/15 text-red-400 border-red-500/30',
    PHASE_2: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
    PHASE_3: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    PHASE_4: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    PHASE_5: 'bg-green-500/15 text-green-400 border-green-500/30',
  };

  return (
    <div className="space-y-5" data-testid="rtp-injury-report">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Total Cases" value={s.total_cases} icon={Shield} />
        <Stat label="Active" value={s.active_cases} warn={s.active_cases > 0} icon={AlertTriangle} />
        <Stat label="Cleared" value={s.cleared_cases} accent icon={CheckCircle} />
        <Stat label="Days Lost" value={s.total_days_lost} warn icon={Calendar} />
      </div>

      {data.cases.length > 0 && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-400 h-8 text-xs"
            data-testid="export-rtp"
            onClick={() => exportCSV('rtp_injury_report.csv',
              ['athlete_name', 'team', 'injury_type', 'start_date', 'end_date', 'current_phase', 'status', 'total_days_in_rtp', 'rtp_sessions'],
              data.cases
            )}>
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
          </Button>
        </div>
      )}

      {/* Desktop table */}
      <div className="overflow-x-auto hidden sm:block">
        <table className="w-full text-sm" data-testid="rtp-table">
          <thead>
            <tr className="border-b border-zinc-800 text-zinc-500 text-xs">
              <th className="text-left py-2 px-3 font-medium">Athlete</th>
              <th className="text-left py-2 px-2 font-medium">Team</th>
              <th className="text-left py-2 px-2 font-medium">Injury</th>
              <th className="text-center py-2 px-2 font-medium">Start</th>
              <th className="text-center py-2 px-2 font-medium">End</th>
              <th className="text-center py-2 px-2 font-medium">Phase</th>
              <th className="text-center py-2 px-2 font-medium">Status</th>
              <th className="text-center py-2 px-2 font-medium">Days</th>
              <th className="text-center py-2 px-2 font-medium">Sessions</th>
            </tr>
          </thead>
          <tbody>
            {data.cases.map(c => (
              <tr key={c.profile_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                <td className="py-2.5 px-3 text-zinc-200 font-medium">{c.athlete_name}</td>
                <td className="py-2.5 px-2 text-zinc-500 text-xs">{c.team}</td>
                <td className="py-2.5 px-2 text-zinc-300 text-xs">{c.injury_type}</td>
                <td className="py-2.5 px-2 text-center text-zinc-400 text-xs font-mono">{c.start_date}</td>
                <td className="py-2.5 px-2 text-center text-zinc-400 text-xs font-mono">{c.end_date || '—'}</td>
                <td className="py-2.5 px-2 text-center">
                  <Badge variant="outline" className={`text-[10px] ${PHASE_COLORS[c.current_phase] || 'border-zinc-700 text-zinc-400'}`}>
                    {c.current_phase?.replace('_', ' ') || '—'}
                  </Badge>
                </td>
                <td className="py-2.5 px-2 text-center">
                  <Badge className={c.status === 'ACTIVE' ? 'bg-rose-500/15 text-rose-400' : 'bg-emerald-500/15 text-emerald-400'}>
                    {c.status}
                  </Badge>
                </td>
                <td className="py-2.5 px-2 text-center text-zinc-300 font-mono text-xs">{c.total_days_in_rtp}</td>
                <td className="py-2.5 px-2 text-center text-zinc-400 font-mono text-xs">{c.rtp_sessions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-2">
        {data.cases.map(c => (
          <div key={`m-${c.profile_id}`} className="p-3 bg-zinc-900/50 border border-zinc-800 rounded-sm">
            <div className="flex justify-between items-start mb-2">
              <div>
                <span className="text-sm text-zinc-200 font-medium block">{c.athlete_name}</span>
                <span className="text-[10px] text-zinc-500">{c.team}</span>
              </div>
              <Badge className={c.status === 'ACTIVE' ? 'bg-rose-500/15 text-rose-400' : 'bg-emerald-500/15 text-emerald-400'}>
                {c.status}
              </Badge>
            </div>
            <div className="text-xs text-zinc-400 mb-1">{c.injury_type}</div>
            <div className="flex gap-3 text-[10px] text-zinc-500 flex-wrap">
              <span>Phase: <Badge variant="outline" className={`text-[9px] ${PHASE_COLORS[c.current_phase] || ''}`}>{c.current_phase?.replace('_', ' ')}</Badge></span>
              <span>Days: <span className="text-zinc-300">{c.total_days_in_rtp}</span></span>
              <span>{c.start_date} → {c.end_date || 'ongoing'}</span>
            </div>
          </div>
        ))}
      </div>

      {data.cases.length === 0 && <div className="text-center py-8 text-zinc-500 text-sm">No RTP cases found in this period</div>}
    </div>
  );
}
